import * as Colors from '@brightlayer-ui/colors';
import { Box, Divider, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChartDataItem, BasicBarChart } from '../../components/BasicBarChart';
import { PaginatedTableComponent } from '../../components/PaginatedTableComponent';
import { StatusGaugeCard } from '../../components/StatusGaugeCard';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { SubscriptionAuditRecord, Status as SubscriptionStatus, Tier } from '../../services/subscription/types';

const styles = {
    divider: {
        marginBottom: '24px',
        backgroundColor: '#424E541F',
    },
};

type EnterpriseSubscriptionDetailsProps = {
    licenseId: string;
    organizationId: string;
    organizationName: string;
    renewalDate: string;
    subscriptionStatus: SubscriptionStatus;
    subscriptionTier: Tier;
    statusGaugeColor: string;
    setShowError: (showError: boolean) => void;
};

export const EnterpriseSubscriptionDetails: React.FC<EnterpriseSubscriptionDetailsProps> = (props) => {
    const {
        licenseId,
        organizationId,
        organizationName,
        renewalDate,
        subscriptionStatus,
        subscriptionTier,
        statusGaugeColor,
        setShowError,
    } = props;

    const { t } = useTranslation();

    type TableData = {
        billingCycle: string;
        deviceOnlineCount: number;
    };

    const [barChartData, setBarChartData] = useState<BarChartDataItem[]>([]);
    const [auditData, setAuditData] = useState<TableData[]>([]);

    const formatAuditData = useCallback((data: SubscriptionAuditRecord[]): BarChartDataItem[] => {
        const dataCopy = data.slice();

        const sortedData = dataCopy.sort(
            (a, b) => DateTime.fromISO(a.billingEndDate).toMillis() - DateTime.fromISO(b.billingEndDate).toMillis()
        );

        const latestTwelveMonths = sortedData.slice(-12);

        const formattedData = latestTwelveMonths.map((record, idx) => {
            const billingDate = DateTime.fromISO(record.billingEndDate, { zone: 'utc' });
            const formattedMonth = billingDate.toFormat('LLL yyyy');
            return {
                id: idx,
                name: formattedMonth,
                value: record.deviceOnlineCount,
                tooltip: `${DateTime.fromISO(record.billingStartDate, { zone: 'utc' }).toFormat(
                    'LLL dd, yyyy'
                )} - ${DateTime.fromISO(record.billingEndDate, { zone: 'utc' }).toFormat('LLL dd, yyyy')}`,
            };
        });

        return formattedData;
    }, []);

    const formatTableData = useCallback(
        (data: SubscriptionAuditRecord[]): TableData[] =>
            data.map((record) => ({
                billingCycle: `${DateTime.fromISO(record.billingStartDate, { zone: 'utc' }).toFormat(
                    'LLL d'
                )} - ${DateTime.fromISO(record.billingEndDate, { zone: 'utc' }).toFormat('LLL d, yyyy')}`,
                deviceOnlineCount: record.deviceOnlineCount,
            })),
        []
    );

    const auditColumns = useMemo(
        () => [
            {
                field: 'billingCycle',
                headerName: t('SUBSCRIPTION_DETAILS_PAGE.BILLING_CYCLE'),
                minWidth: 170,
                format: (row: SubscriptionAuditRecord): string =>
                    `${DateTime.fromISO(row.billingStartDate, { zone: 'utc' }).toFormat(
                        'LLL d, yyyy'
                    )} - ${DateTime.fromISO(row.billingEndDate, { zone: 'utc' }).toFormat('LLL d, yyyy')}`,
            },
            {
                field: 'deviceOnlineCount',
                headerName: t('SUBSCRIPTION_DETAILS_PAGE.CONNECTED_DEVICES'),
                minWidth: 100,
                format: (row: SubscriptionAuditRecord): number => row.deviceOnlineCount,
            },
        ],
        [t]
    );

    useEffect(() => {
        const fetchAuditData = async (): Promise<void> => {
            try {
                const [fetchedAuditData] = await Promise.all([
                    SubscriptionService.getSubscriptionAuditOfOrganization(organizationId),
                ]);
                setBarChartData(formatAuditData(fetchedAuditData));
                setAuditData(formatTableData(fetchedAuditData));
                setShowError(false);
            } catch {
                setShowError(true);
            }
        };
        void fetchAuditData();
    }, [organizationId, t]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '98.5%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 0 auto',
                    flexDirection: 'column',
                    margin: '32px',
                    padding: '8px',
                    backgroundColor: Colors.white[50],
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: '24px',
                        flexDirection: 'row',
                    }}
                >
                    <Box>
                        <Typography color={Colors.gray[800]} variant="h6" sx={{ marginBottom: '8px' }}>
                            {organizationName} {t('SUBSCRIPTION_DETAILS_PAGE.LICENSE_DETAILS')}
                        </Typography>
                        <Typography color={Colors.gray[800]} variant="subtitle2" sx={{ marginBottom: '8px' }}>
                            <b>{t('SUBSCRIPTION_DETAILS_PAGE.LICENSE_KEYS')}: </b>
                            {licenseId}
                        </Typography>
                        <Typography color={Colors.gray[800]} variant="subtitle2" sx={{ marginBottom: '8px' }}>
                            <b>{t('SUBSCRIPTION_DETAILS_PAGE.NEXT_BILLING_CYCLE_ENDS')}: </b>
                            {DateTime.fromISO(renewalDate).toLocaleString(DateTime.DATE_FULL)}
                        </Typography>
                    </Box>

                    <StatusGaugeCard
                        data-testid="subscription-status-card"
                        title={t('ORGANIZATION_DETAILS_PAGE.TIER')}
                        status={subscriptionStatus}
                        subtitle1={subscriptionTier}
                        color={statusGaugeColor}
                    />
                </Box>
                <Box
                    sx={{
                        margin: '0 24px',
                    }}
                >
                    <Divider sx={styles.divider} />
                    <BasicBarChart
                        data-testid="subscription-bar-chart"
                        dataset={barChartData}
                        height={310}
                        color={Colors.blue[500]}
                        title={t('SUBSCRIPTION_DETAILS_PAGE.BAR_CHART_TITLE')}
                        units={t('SUBSCRIPTION_DETAILS_PAGE.BAR_CHART_UNITS')}
                    />
                </Box>

                <Box
                    sx={{
                        margin: '0 24px',
                    }}
                >
                    <Divider sx={styles.divider} />
                    <PaginatedTableComponent
                        title={t('SUBSCRIPTION_DETAILS_PAGE.TABLE_TITLE')}
                        dataset={auditData}
                        columns={[
                            {
                                ...auditColumns[0],
                                customSort: (a: string, b: string): number => {
                                    const getEndDate = (billingCycle: string): DateTime => {
                                        const match = /-\s(.+)$/.exec(billingCycle);
                                        return match
                                            ? DateTime.fromFormat(match[1], 'LLL d, yyyy')
                                            : DateTime.invalid('');
                                    };
                                    const dateA = getEndDate(a);
                                    const dateB = getEndDate(b);
                                    return dateA.toMillis() - dateB.toMillis();
                                },
                            },
                            auditColumns[1],
                        ]}
                        rowsPerPageOptions={[5, 10, 20]}
                        defaultRowsPerPage={5}
                        labelRowsPerPage={t('SUBSCRIPTION_DETAILS_PAGE.LABEL_ROWS_PER_PAGE')}
                        data-testid="subscription-audit-table"
                        emptyStateMessage={t('SUBSCRIPTION_DETAILS_PAGE.EMPTY_TABLE_MESSAGE')}
                    />
                </Box>
            </Box>
        </Box>
    );
};
